import { createContext, useContext, useEffect, useState } from "react";
import { useGetUserById } from "../hooks/useUser";
import { getUserById, updateUserById } from "../containers/newBackendApi/newUser";
import { apiGetMe } from "../containers/newBackendApi/auth";

const UserContext = createContext();

// Decode a JWT payload without a library (fallback identity source when the
// session cookie / /auth/me is unavailable and only the bearer token is held).
const decodeJwt = (token) => {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, "=")));
  } catch {
    return null;
  }
};

// Resolve the signed-in identity server-side (GET /auth/me), falling back to the
// locally held bearer token. Replaces the old Amplify Auth.currentAuthenticatedUser.
const resolveIdentity = async () => {
  const me = await apiGetMe();
  if (me?.sub) return { sub: me.sub, claims: me };
  const claims = decodeJwt(localStorage.getItem("tk"));
  if (claims?.sub) return { sub: claims.sub, claims };
  return null;
};

const detailsFromClaims = (claims = {}) => ({
  user_first_name: claims.given_name || claims.name || "",
  user_last_name: claims.family_name || "",
  user_email: claims.email || "",
  user_phone_number: claims.phone_number || "",
});

export const UserProviderContext = ({ children, isAuthenticated }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sub, setSub] = useState(null);

  const { data: dbUser, refetch: refetchDbUser } = useGetUserById(sub);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const identity = await resolveIdentity();
      if (!identity?.sub) {
        console.error("Error in fetchUser: could not resolve identity");
        return;
      }
      setSub(identity.sub);

      try {
        const userResponse = await getUserById(identity.sub);
        if (userResponse) {
          setUserDetails(userResponse);
        } else {
          // Fallback to the identity claims (from /auth/me or the bearer token).
          setUserDetails(detailsFromClaims(identity.claims));
        }
      } catch (apiErr) {
        // /get_user is session-cookie authed (accounts service); this app is
        // bearer-primary, so a 401/403 here just means there's no session cookie
        // — the identity claims are an authoritative fallback, not an error.
        // Only surface genuinely unexpected failures (5xx, network, etc.).
        const status = apiErr?.response?.status;
        if (status !== 401 && status !== 403) {
          console.error("Failed to fetch user from DB, falling back to token claims:", apiErr);
        }
        setUserDetails(detailsFromClaims(identity.claims));
      }
    } catch (err) {
      console.error("Error in fetchUser:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updatedFields) => {
    setError("");
    setSuccess("");
    try {
      if (!sub) throw new Error("User not loaded");
      await updateUserById(sub, updatedFields);
      setSuccess("Profile updated successfully!");
      await fetchUser();
      return true;
    } catch (err) {
      setError(err.message || "Failed to update profile");
      return false;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setUserDetails(null);
      return;
    }
    fetchUser();
  }, [isAuthenticated]);

  return (
    <UserContext.Provider
      value={{
        userDetails,
        dbUser,
        loading,
        error,
        success,
        sub,
        handleSave,
        fetchUser,
        refetchDbUser,
        clearMessages: () => { setError(""); setSuccess(""); },
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProviderContext");
  }
  return context;
};
